import cn from "classnames";
import Link from "next/link";
import { useState } from "react";
import { Typography } from "@mui/material";

import { BaseButton } from "@/src/component/base/BaseButton";
import { DrawerWithExit } from "@/src/component/base/DrawerWithExit";
import { ProviderHooks } from "@/src/hook";
import { ComponentType, StrapiComponentType } from "@/src/type";

import { SectionModule, TextBreakpointModule } from "@/src/component/partial/__module__";
import { ContactUsFormMRI } from "./component";

import theme from "@/styles/scss/theme.module.scss";
import styles from "./ContactUs.module.scss";

interface ContactUsProps extends ComponentType.DynamicPageSectionComponentProps<StrapiComponentType.Section.ContactUs> {}

function ContactUs({ section, slug, snapToGrid, classes, searchParams, lazy, quality }: ContactUsProps) {
  const contactUs: StrapiComponentType.Section.ContactUs = section.type?.[0];
  const cms = ProviderHooks.useCMS();
  const [loading, setLoading] = useState<boolean>(false);
  const [openForm, setOpenForm] = useState<boolean>(false);

  return (
    <SectionModule
      section={section}
      slug={slug}
      stylePrefix="ContactUs"
      styleContainerId={contactUs.id}
      snapToGrid={snapToGrid}
      classes={{
        section: classes?.section,
      }}
      searchParams={searchParams}
      lazy={lazy}
      quality={quality}
    >
      <>
        <div className={styles.container}>
          <div className={styles.textContainer}>
            <TextBreakpointModule
              id={`contactUs_title_${contactUs.id}`}
              text={section.title}
              className={styles.title}
            />
            <TextBreakpointModule
              id={`contactUs_desc_${contactUs.id}`}
              text={contactUs.description}
              className={styles.description}
            />
          </div>
          <div className={styles.formContainer}>
            <div className={cn(styles.agentMessageContainer, styles.desktop)}>
              <Typography
                className={styles.brokerCheckboxLabel}
                color={theme.secondaryMain}
              >
                {contactUs.formBrokerCheckboxLabel}
              </Typography>
              <BaseButton
                type="button"
                variant="outlined"
                color="secondary"
                size="large"
                className={styles.registerBtn}
                component={Link}
                href={cms.constructLink("/broker/register/identity")}
              >
                {contactUs.borkerRegisterButtonText}
              </BaseButton>
            </div>
          </div>
          <div className={styles.formContainer}>
            <ContactUsFormMRI
              loading={loading}
              setLoading={setLoading}
              contactUs={contactUs}
              isDark={false}
            />
          </div>
          <div className={styles.formOpen}>
            <BaseButton
              type="submit"
              variant="outlined"
              color="secondary"
              size="large"
              onClick={() => setOpenForm(true)}
            >
              {contactUs.formSubmitButtonLabel}
            </BaseButton>
          </div>
          <DrawerWithExit
            drawer={{
              open: openForm,
              onClose: () => setOpenForm(!openForm),
            }}
          >
            <div className={styles.modalContainer}>
              <div className={styles.modalForm}>
                <div className={styles.textContainer}>
                  <TextBreakpointModule
                    id={`contactUs_title_${contactUs.id}`}
                    text={section.title}
                    className={cn(styles.title, styles.dark)}
                  />
                  <TextBreakpointModule
                    id={`contactUs_desc_${contactUs.id}`}
                    text={contactUs.description}
                    className={cn(styles.description, styles.dark)}
                  />
                </div>
                <div className={cn(styles.agentMessageContainer, styles.mobile)}>
                  <Typography
                    className={styles.brokerCheckboxLabel}
                    color={theme.secondaryMain}
                  >
                    {contactUs.formBrokerCheckboxLabel}
                  </Typography>
                  <BaseButton
                    type="button"
                    variant="outlined"
                    color="secondary"
                    size="large"
                    className={styles.registerBtn}
                    component={Link}
                    href={cms.constructLink("/broker/register/identity")}
                  >
                    {contactUs.borkerRegisterButtonText}
                  </BaseButton>
                </div>
                <ContactUsFormMRI
                  loading={loading}
                  setLoading={setLoading}
                  isDark={true}
                  contactUs={contactUs}
                />
              </div>
            </div>
          </DrawerWithExit>
        </div>
      </>
    </SectionModule>
  );
}

export default ContactUs;
